<template>
  <div>
    <v-row>
      <v-col cols="12" :sm="isInline ? 4 : 5">
        <a-text-field
          class="textField"
          v-model="form.topicName"
          :label="$t('topic')"
          :requiredMessage="$t('agenda.ruleTopicNameReq')"
          required
          :hide-details="isTopicNameExist"
          filled
          dense
          :validationMessage="$t('lblValidateTopicName')"
        ></a-text-field>
        <span
          v-if="isTopicNameExist"
          class="label-s"
          style="color: var(--v-error-base) !important"
          >{{ message }}</span
        >
      </v-col>
      <v-col cols="12" sm="4">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div v-on="on">
              <v-select
                v-bind="attrs"
                v-model="form.topicCategory"
                :items="topicCategories"
                :label="`${$t('agenda.hTopicCategory')} *`"
                item-text="name"
                item-value="id"
                :menu-props="{ bottom: true, offsetY: true }"
                :rules="[(v) => !!v || $t('agenda.ruleCategoryNameReq')]"
                required
                filled
                class="ml-2 category"
                dense
              ></v-select>
            </div>
          </template>
          <span v-html="$t('agenda.tipTopicCategoryField')"></span>
        </v-tooltip>
      </v-col>
      <v-col cols="12" sm="3">
        <v-text-field
          v-model.trim="form.timeInMinutes"
          :label="$t('agenda.lblTimeInMin')"
          type="number"
          :min="0"
          :max="9999"
          :rules="[(v) => validateTimeReqRule(v), (v) => validateTimeLength(v)]"
          required
          :maxlength="timeDigitTextLimit"
          pattern="/^-?\d+\.?\d*$/"
          onKeyPress="if(this.value.length==maxLength) { return false; }"
          @keydown="filterKey"
          @input="filterNumberInput"
          filled
          class="ml-2"
          dense
        ></v-text-field>
      </v-col>
      <v-col v-if="isInline" cols="12" sm="1" class="text-right">
        <div>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <a-btn
                v-bind="attrs"
                v-on="on"
                x-small
                width="24"
                min-width="24"
                color="primaryDark"
                elevation="1"
                @click="$emit('saveTopic')"
                :disabled="isTopicNameExist"
              >
                <v-icon size="16">mdi-check</v-icon>
              </a-btn>
            </template>
            <span>{{ $t("agenda.lblSaveTopic") }} </span>
          </v-tooltip>
        </div>
        <div class="my-2">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <a-btn
                v-bind="attrs"
                v-on="on"
                x-small
                color="primaryDark"
                elevation="1"
                width="24"
                min-width="24"
                @click="$emit('cancel')"
                class="ml-3"
              >
                <v-icon size="16"> mdi-close </v-icon>
              </a-btn>
            </template>
            <span>{{ $t("agenda.lblCloseTopic") }} </span>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="!isInline" class="mt-0">
      <v-col cols="12" sm="12" class="text-right pt-1">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              class="ml-2"
              x-small
              color="primaryDark"
              v-bind="attrs"
              v-on="on"
              @click="$emit('saveTopic')"
              :disabled="isTopicNameExist"
            >
              {{ okCancelButtons ? $t("btnOk") : $t("btnSave") }}
            </a-btn>
          </template>
          <span>{{ $t("agenda.lblSaveTopic") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              class="ml-2"
              x-small
              color="secondary"
              @click="$emit('cancel')"
              v-bind="attrs"
              v-on="on"
            >
              {{ $t("btnClose") }}
            </a-btn>
          </template>
          <span>{{ $t("agenda.lblCloseTopic") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { timeDigitTextLimit } from "../../utils/constant";
import SignalRMixin from "../../mixin/SignalRMixin";

export default {
  name: "AddAgendaTopicDetails",
  mixins: [SignalRMixin],
  props: {
    value: {
      type: Object,
      required: () => {},
    },
    agendaDetail: Object,
    oldTopicName: {
      type: String,
      required: "",
    },
    isInline: {
      type: Boolean,
      required: false,
    },
    okCancelButtons: {
      type: Boolean,
      required: false,
    },
    archivedTopics:{
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      timeDigitTextLimit: timeDigitTextLimit,
      validationMsg: this.$t("agenda.msgTopicNameUnique"),
      topic: {
        Id: "",
        agendaId: null,
        topicName: "",
        timeInMinutes: 0,
        topicCategory: "",
        actualTime: 0,
        peopleInCharge: [],
        documents: [],
        isArchived: false,
        memoDescription: ""
      },
    };
  },
  mounted() {
    this.signalRInit(`tenant_config_${this.selectedTenant}`);
  },
  computed: {
    ...gettersMap(),
    form: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    message: {
      get() {
        return this.validationMsg;
      },
      set(msg) {
        this.validationMsg = msg;
      },
    },
    isTopicNameExist() {
      const { topicName: formTopicName } = this.form;
      const { topics } = this.agendaDetail;

      // Helper function to clean and normalize topic names
      const normalize = (name) => String(name).toLowerCase().trim();

      // Normalized values
      const oldTopicName = normalize(this.oldTopicName);
      const newTopicName = normalize(formTopicName);
      const topicNames = this.archivedTopics.map(item => item.toLowerCase());

      // Check if the topic exists in agenda topics
      const exist = topics.find((t) => oldTopicName !== newTopicName && normalize(t.topicName) === newTopicName)
      
      if (exist && exist.is_archived) {
        this.validationMsg = this.$t("agenda.msgArchiveTopicNameUnique");
        return true;
      } else if (exist) {
        this.validationMsg = this.$t("agenda.msgTopicNameUnique");
        return true;
      } else if (topicNames.includes(newTopicName)) {
        this.validationMsg = this.$t("agenda.msgArchiveTopicNameUnique");
        return true;
      }

      // If not exist in current topics or archivedTopics
      return false;
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    topicCategories() {
      return this.tenantSettingTypes("topiccategory");
    },
  },
  methods: {
    ...mapActions({
      getTenantConfigList: "tenant/getUpdatedTenantConfigList",
    }),
    filterKey,
    filterNumberInput,
    validateTimeLength,
    validateTimeReqRule,
    signalrConfigTarget,
    signalRInit,
  },
};

async function signalRInit(target) {
  await this.signalConnection.on(target, async (message) => {
    if (message.config_name && message.config_name === "topiccategory") {
      await this.getTenantConfigList();
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

async function signalrConfigTarget(message) {
  if (message.config_name && message.config_name === "topiccategory") {
    await this.getTenantConfigList();
  }
}

function validateTimeReqRule(value) {
  return (
    (value.toString() !== "" && Number(value) > -1) ||
    this.$t("agenda.ruleTimeReq")
  );
}

function validateTimeLength(value) {
  return (
    (value !== null && value.toString().length < 5) ||
    this.$t("agenda.ruleTimeMaxlength")
  );
}

function filterKey(e) {
  const key = e.key;
  if (key === ".") return e.preventDefault();
  if (key === "e") return e.preventDefault();
  if (key === "-") return e.preventDefault();
}

function filterNumberInput(value) {
  if (value) {
    // Convert to string and remove non-numeric characters
    let filtered = value.toString().replace(/[^0-9]+/g, "");

    // Remove leading zero if it exists
    filtered = filtered.replace(/^0+/, "");

    // Update the timeInMinutes property
    this.form.timeInMinutes = filtered ? filtered : "0";
  } else {
    this.form.timeInMinutes = "0";
  }
}

function gettersMap() {
  return mapGetters({
    tenantSettingTypes: "tenant/tenantSettingTypes",
  });
}
</script>

<style scoped>
::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details {
  margin-bottom: 0px !important;
}
.category /deep/ .v-select__selection {
  line-height: 21px !important;
}
</style>